import { 
  format, 
  parse, 
  startOfDay, 
  endOfDay, 
  startOfWeek,
  endOfWeek,
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear, 
  addDays,
  addWeeks, 
  addMonths, 
  addYears, 
  isSameDay, 
  isSameMonth,
  isSameWeek, 
  isWithinInterval,
  getWeek 
} from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Formatea una fecha según el formato especificado
 * @param {Date} date - Fecha a formatear
 * @param {string} dateFormat - Formato de fecha
 * @returns {string} Fecha formateada
 */
export function formatDate(date, dateFormat = 'dd/MM/yyyy') {
  return format(date, dateFormat, { locale: es });
}

/**
 * Formatea una hora según el formato especificado
 * @param {Date} date - Fecha con hora
 * @param {string} timeFormat - Formato de hora
 * @returns {string} Hora formateada
 */
export function formatTime(date, timeFormat = 'HH:mm') {
  return format(date, timeFormat, { locale: es });
}

/**
 * Parsea una fecha desde un string
 * @param {string} dateString - String con la fecha
 * @param {string} dateFormat - Formato de la fecha
 * @returns {Date} Fecha parseada
 */
export function parseDate(dateString, dateFormat = 'dd/MM/yyyy') {
  return parse(dateString, dateFormat, new Date(), { locale: es });
}

/**
 * Parsea una fecha desde formato ISO (YYYY-MM-DD) sin problemas de zona horaria
 * Crea la fecha en la zona horaria local, no en UTC
 * @param {string} dateString - Fecha en formato YYYY-MM-DD
 * @returns {Date} Fecha en zona horaria local
 */
export function parseDateFromBackend(dateString) {
  if (!dateString) return new Date();
  
  // Si es un objeto Date, retornarlo directamente
  if (dateString instanceof Date) return dateString;
  
  // Extraer año, mes, día del string YYYY-MM-DD
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  
  // Crear fecha en zona horaria local (mes es 0-indexed en JavaScript)
  return new Date(year, month - 1, day);
}

/**
 * Obtiene el inicio del día
 * @param {Date} date - Fecha
 * @returns {Date} Inicio del día
 */
export function getStartOfDay(date) {
  return startOfDay(date);
}

/**
 * Obtiene el fin del día
 * @param {Date} date - Fecha
 * @returns {Date} Fin del día
 */
export function getEndOfDay(date) {
  return endOfDay(date);
}

/**
 * Obtiene el inicio del mes
 * @param {Date} date - Fecha
 * @returns {Date} Inicio del mes
 */
export function getStartOfMonth(date) {
  return startOfMonth(date);
}

/**
 * Obtiene el fin del mes
 * @param {Date} date - Fecha
 * @returns {Date} Fin del mes
 */
export function getEndOfMonth(date) {
  return endOfMonth(date);
}

/**
 * Obtiene el inicio del año
 * @param {Date} date - Fecha
 * @returns {Date} Inicio del año
 */
export function getStartOfYear(date) {
  return startOfYear(date);
}

/**
 * Obtiene el fin del año
 * @param {Date} date - Fecha
 * @returns {Date} Fin del año
 */
export function getEndOfYear(date) {
  return endOfYear(date);
}

/**
 * Añade días a una fecha
 * @param {Date} date - Fecha base
 * @param {number} days - Número de días a añadir
 * @returns {Date} Nueva fecha
 */
export function addDaysToDate(date, days) {
  return addDays(date, days);
}

/**
 * Añade meses a una fecha
 * @param {Date} date - Fecha base
 * @param {number} months - Número de meses a añadir
 * @returns {Date} Nueva fecha
 */
export function addMonthsToDate(date, months) {
  return addMonths(date, months);
}

/**
 * Añade años a una fecha
 * @param {Date} date - Fecha base
 * @param {number} years - Número de años a añadir
 * @returns {Date} Nueva fecha
 */
export function addYearsToDate(date, years) {
  return addYears(date, years);
}

/**
 * Verifica si dos fechas son el mismo día
 * @param {Date} date1 - Primera fecha
 * @param {Date} date2 - Segunda fecha
 * @returns {boolean} True si son el mismo día
 */
export function isSameDayAs(date1, date2) {
  return isSameDay(date1, date2);
}

/**
 * Verifica si dos fechas son el mismo mes
 * @param {Date} date1 - Primera fecha
 * @param {Date} date2 - Segunda fecha
 * @returns {boolean} True si son el mismo mes
 */
export function isSameMonthAs(date1, date2) {
  return isSameMonth(date1, date2);
}

/**
 * Verifica si una fecha está dentro de un intervalo
 * @param {Date} date - Fecha a verificar
 * @param {Date} start - Inicio del intervalo
 * @param {Date} end - Fin del intervalo
 * @returns {boolean} True si está dentro del intervalo
 */
export function isDateInInterval(date, start, end) {
  return isWithinInterval(date, { start, end });
}

/**
 * Obtiene todos los días de un mes
 * @param {Date} date - Fecha del mes
 * @returns {Date[]} Array con todos los días del mes
 */
export function getDaysInMonth(date) {
  const start = getStartOfMonth(date);
  const end = getEndOfMonth(date);
  const days = [];
  
  let currentDay = start;
  while (currentDay <= end) {
    days.push(currentDay);
    currentDay = addDays(currentDay, 1);
  }
  
  return days;
}

/**
 * Obtiene todos los días de un año
 * @param {Date} date - Fecha del año
 * @returns {Date[]} Array con todos los días del año
 */
export function getDaysInYear(date) {
  const start = getStartOfYear(date);
  const end = getEndOfYear(date);
  const days = [];
  
  let currentDay = start;
  while (currentDay <= end) {
    days.push(currentDay);
    currentDay = addDays(currentDay, 1);
  }
  
  return days;
}

/**
 * Obtiene el nombre del día de la semana
 * @param {Date} date - Fecha
 * @param {boolean} short - Si usar versión corta
 * @returns {string} Nombre del día
 */
export function getDayName(date, short = false) {
  return format(date, short ? 'EEE' : 'EEEE', { locale: es });
}

/**
 * Obtiene el nombre del mes
 * @param {Date} date - Fecha
 * @param {boolean} short - Si usar versión corta
 * @returns {string} Nombre del mes
 */
export function getMonthName(date, short = false) {
  return format(date, short ? 'MMM' : 'MMMM', { locale: es });
}

/**
 * Calcula la duración entre dos fechas en minutos
 * @param {Date} start - Fecha de inicio
 * @param {Date} end - Fecha de fin
 * @returns {number} Duración en minutos
 */
export function getDurationInMinutes(start, end) {
  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
}

/**
 * Convierte minutos a formato legible (ej: "2h 30m")
 * @param {number} minutes - Minutos
 * @returns {string} Duración formateada
 */
export function formatDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}m`;
  }
  
  if (mins === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${mins}m`;
}

/**
 * Obtiene el inicio de la semana (domingo)
 * @param {Date} date - Fecha
 * @returns {Date} Inicio de la semana
 */
export function getStartOfWeek(date) {
  return startOfWeek(date, { weekStartsOn: 0, locale: es }); // 0 = Domingo
}

/**
 * Obtiene el fin de la semana (sábado)
 * @param {Date} date - Fecha
 * @returns {Date} Fin de la semana
 */
export function getEndOfWeek(date) {
  return endOfWeek(date, { weekStartsOn: 0, locale: es }); // 0 = Domingo
}

/**
 * Obtiene todos los días de una semana (domingo a sábado)
 * @param {Date} date - Fecha dentro de la semana
 * @returns {Date[]} Array con los 7 días de la semana
 */
export function getDaysInWeek(date) {
  const start = getStartOfWeek(date);
  const days = [];
  
  for (let i = 0; i < 7; i++) {
    days.push(addDays(start, i));
  }
  
  return days;
}

/**
 * Obtiene el número de semana del año
 * @param {Date} date - Fecha
 * @returns {number} Número de semana (1-53)
 */
export function getWeekNumber(date) {
  return getWeek(date, { weekStartsOn: 0, locale: es });
}

/**
 * Añade semanas a una fecha
 * @param {Date} date - Fecha base
 * @param {number} weeks - Número de semanas a añadir
 * @returns {Date} Nueva fecha
 */
export function addWeeksToDate(date, weeks) {
  return addWeeks(date, weeks);
}

/**
 * Verifica si dos fechas están en la misma semana
 * @param {Date} date1 - Primera fecha
 * @param {Date} date2 - Segunda fecha
 * @returns {boolean} True si están en la misma semana
 */
export function isSameWeekAs(date1, date2) {
  return isSameWeek(date1, date2, { weekStartsOn: 0, locale: es });
}



